import {
  AirtableAssetResponse,
  AirtableRecordResponse,
} from "../airtable/types";
import { fetchAssetDetails } from "../FetchAssetDetails";
import { AssetData } from "./types";

/**
 * Fetches and combines asset data from both Airtable and ATOM (Peacock API)
 *
 * @remarks
 * This utility function serves as a bridge between Airtable's custom content management
 * data and the production ATOM API. It enriches Airtable records with full asset details
 * from the Peacock platform by using the `Atom Slug` field as the lookup key.
 *
 * @param asset - AirtableRecordResponse containing the asset record from Airtable
 * @returns Promise that resolves to an AssetData object containing:
 *   - `id` - The Airtable record ID
 *   - `airtable` - Custom metadata from Airtable
 *   - `atom` - Full production asset details from the ATOM API
 * @throws Error if the `Atom Slug` field is undefined in the Airtable record
 *
 * @example
 * ```typescript
 * const airtableRecord = await fetchFromAirtable("recABC123");
 * const assetData = await fetchAssetData(airtableRecord);
 * // assetData now contains both Airtable custom fields and ATOM production data
 * console.log(assetData.airtable["Custom Field"]);
 * console.log(assetData.atom.attributes.name);
 * ```
 */
const fetchAssetData = async (
  asset: AirtableRecordResponse,
): Promise<AssetData> => {
  const airtable = asset.fields as AirtableAssetResponse;
  if ("Atom Slug" in airtable && airtable["Atom Slug"] !== undefined) {
    const atomResult = await fetchAssetDetails(airtable["Atom Slug"]);
    if (atomResult === null) {
      throw new Error(`Failed to fetch asset ${asset.id}`);
    }

    return { id: asset.id, airtable, atom: atomResult };
  } else {
    throw new Error(`'Atom Slug' is undefined for the asset ${asset.id}`);
  }
};

export { fetchAssetData };
